import React, { Component, Fragment, useState, useEffect, useRef } from 'react';
import { Input, Dropdown } from 'semantic-ui-react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { stopEventPropagation } from '../helpers';

import './easy_table.css';

const TextRenderer = React.memo((props) => {
  const { className, style, placeholder, row, rowIndex, column, oldValue, editable, onContextMenu, onMouseDown } = props;

  const [editing, setEditing] = useState(false);
  const textAreaRef = useRef(null);

  const onTextareaChange = (evt) => {
    props.onTextareaChange(evt, oldValue, row, column, rowIndex);
  }

  const onTextareaBlur = (evt) => {
    props.onTextareaBlur(evt, rowIndex);
  }

  const onTextAreaFocus = (evt) => {
    props.onTextAreaFocus(evt, row, column);
  }

  const onClick = (evt) => {
    setEditing(true);
  }

  const onBlur = (evt) => {
    setEditing(false);
  }

  useEffect(()=>{
    if (textAreaRef.current) {
      const currentElement = textAreaRef.current;
      currentElement.focus();

      const textLength = currentElement.value.length;
      currentElement.setSelectionRange(textLength, textLength);
    }
  }, [editing])

  const staticTextProps = { className, style, onContextMenu, onMouseDown }
  const componentProps = editable ? { ...staticTextProps, onClick, onBlur } : staticTextProps;

  return (
    <div {...componentProps}>
      {
        editing
        ?
        <textarea
          ref={textAreaRef}
          className='easy-table-textarea'
          defaultValue={row[column.dataField]}
          onChange={onTextareaChange}
          onBlur={onTextareaBlur}
          onFocus={onTextAreaFocus}
          onContextMenu={stopEventPropagation}
          placeholder={placeholder}
        >
        </textarea>
        :
        <Fragment>
          {
            column.formatter
            ?
            <div style={column.cellInnerDivStyle}>{column.formatter(row[column.dataField], row, rowIndex, column)}</div>
            :
            <div style={column.cellInnerDivStyle}>{row[column.dataField]}</div>
          }
        </Fragment>
      }
    </div>
  )
}, (prevProps, nextProps) => {
  return prevProps.row === nextProps.row &&
         prevProps.rowIndex === nextProps.rowIndex &&
         prevProps.column === nextProps.column &&
         prevProps.oldValue === nextProps.oldValue &&
         prevProps.editable === nextProps.editable &&
         prevProps.className === nextProps.className &&
         prevProps.placeholder === nextProps.placeholder;
});

const DropdownRenderer = React.memo((props) => {
  const { className, style, row, rowIndex, column, editable } = props;

  const [editing, setEditing] = useState(false);

  const onDropdownChange = (evt, obj) => {
    props.onDropdownChange(evt, obj, row[column.dataField], row, column, rowIndex);
  };

  const onMouseEnter = (evt) => {
    setEditing(true);
  }

  const onMouseLeave = (evt) => {
    setEditing(false);
  }

  const componentProps = editable ? { className, style, onMouseEnter, onMouseLeave } : { className, style };

  return (
    <div {...componentProps}>
      {
        editing
        ?
        <div
          className='easy-table-dropdown'
        >
          <Dropdown
            fluid
            selection
            floating
            options={ column.editor.options }
            value={ row[column.dataField] }
            onChange={onDropdownChange}
            forceSelection={false}
            selectOnBlur={false}
          />
        </div>
        :
        <Fragment>
        {
          column.formatter
          ?
          <div className='easy-table-dropdown-text'>{column.formatter(row[column.dataField], row, rowIndex, column)}</div>
          :
          <div className='easy-table-dropdown-text'>{row[column.dataField]}</div>
        }
        </Fragment>
      }
    </div>
  )
}, (prevProps, nextProps) => {
  return prevProps.row === nextProps.row &&
         prevProps.rowIndex === nextProps.rowIndex &&
         prevProps.column === nextProps.column &&
         prevProps.editable === nextProps.editable &&
         prevProps.className === nextProps.className;
});

class EasyTable extends Component {
  constructor(props){
    super(props);
    this.state = {
      noDataMessage: 'No data loaded. Please load or create one',
      defaultStyle: { width: '100%', height: '100%' },
      data: this.props.data,
      sort: this.props.defaultSort,
      row: {},
      column: {},
      oldValue: '',
      newValue: '',
      inputRefs: {}
    }

    this.isChanging = false;

  }

  componentWillReceiveProps = (nextProps) => {
    if(nextProps.data !== this.props.data){
      this.setState({
        data: nextProps.data,
      })
    }

    if(nextProps.columns !== this.props.columns){
      const refs = {}
      nextProps.columns.map((column, i)=>{
        if (column.hidden) return;
        refs[`filter_${column.dataField}`] = column.filter ? column.filter.defaultValue : '';
        return true
      })
      this.setState({
        inputRefs: refs
      })
    }

  }

  isFunction = (object) => {
   return typeof(object) === 'function';
  }

  sortBy = (dataField) => {
    if(this.isChanging){
      setTimeout(()=>{
        console.log('Canceling sort while update is in progress.');
      },2000)
      this.isChanging = false
      return
    }
    let sort = { ...this.state.sort }
    if(dataField !== sort.sortField){
      sort.sortOrder = 'asc'
    }
    else {
      sort.sortOrder = sort.sortOrder === 'asc' ? 'desc' : 'asc'
    }
    sort.sortField = dataField
    this.setState({
      sort: sort
    })
    this.props.columnEvent('sort', sort)
  }

  onInputChange = (evt, field) => {
    const inputRefs = { ...this.state.inputRefs, [evt.target.name]: evt.target.value };
    this.setState({
      inputRefs: inputRefs,
      currentInputRef: evt.target.name,
      currentInputValue: evt.target.value
    }, () => {
      const state = {
        filters: {}
      }
      state.filters[field] = {
        filterVal: this.state.currentInputValue
      }
      this.props.columnEvent('filter', state)
    })
  }

  onInputFocus = (evt, field) => {
    const focusedId = evt.target.id;
    const { currentInputRef, currentInputValue } = this.state;
    const refs = Object.getOwnPropertyNames(this.state.inputRefs)
    if(refs.length > 0){
      refs.map((ref)=>{
        if (ref === focusedId) return;

        const element = document.getElementById(ref)
        if (element) element.value = '';
      })
    }

    if (evt.target.name !== currentInputRef && currentInputValue) {
      this.onInputChange(evt, field);
    };
    
  }

  renderColumns = (props) => {
    const { columns, lockedColumnHeadings, translations, readOnly=false } = props
    return (
      <div key='head' className={`easy-table-heading-row ${lockedColumnHeadings ? 'locked' : ''}`}>
        {
          columns.map((column, colIndex)=>{
            if(column.hidden) return;

            let headProps = {
              key: `head-${column.dataField}`,
              className: 'easy-table-heading-cell',
              title: column.text
            }

            if (!readOnly) headProps.onClick = (evt) => this.sortBy(column.dataField);

            if(column.headerStyle){
              headProps.style = column.headerStyle
            }
            if(column.headerClasses){
              headProps.className = `${headProps.className} ${column.headerClasses}`
            }
            const inputRef = `filter_${column.dataField}`
            let filterElement = ''
            if(column.filter){
              filterElement = (
                <Input
                  key={inputRef}
                  ref={inputRef}
                  name={inputRef}
                  id={inputRef}
                  className='easy-table-input'
                  placeholder={`${translations.search} ${column.text}`}
                  defaultValue={this.state.inputRefs[inputRef]}
                  onChange={(evt)=>this.onInputChange(evt, column.dataField)}
                  onClick={(evt)=>evt.stopPropagation()}
                  onFocus={(evt)=>this.onInputFocus(evt, column.dataField)}
                >
                </Input>
              )
            }
            return (
              <div {...headProps}>
                {
                  column.headerFormatter && column.headerFormatter(column, colIndex, { filterElement })
                }
                {
                  !column.headerFormatter && column.text
                }
              </div>
            )
          })
        }
      </div>
    )
  }

  onTextAreaFocus = (evt, row, column) => {
    this.setState({
      oldValue: evt.target.value,
      newValue: evt.target.value,
      row: row,
      column: column
    })
  }

  onTextareaChange = (evt, oldValue, row, column, rowIndex) => {
    this.isChanging = true
    const updatedRow = { ...row, [column.dataField]: evt.target.value };
    const data = this.state.data.map((item, index) =>
      index === rowIndex ? updatedRow : item
    );
    this.setState({
      oldValue: oldValue,
      row: updatedRow,
      column: column,
      newValue: evt.target.value,
      data: data,
    })
  }

  onTextareaBlur = (evt, rowIndex) => {
    const { oldValue, newValue, row, column } = this.state
    if (column.validator && !column.validator(newValue)){
      this.resetTextareaValue(evt, rowIndex);
      return;
    }
    this.props.cellEvent(oldValue, newValue, row, column)
  }

  resetTextareaValue = (evt, rowIndex) =>{
    const { oldValue, column } = this.state
    let { newValue } = this.state
    evt.target.value = oldValue
    newValue = oldValue
    const updatedRow = { ...this.state.row, [column.dataField]: oldValue };
    const data = this.state.data.map((item, index) =>
      index === rowIndex ? updatedRow : item
    );
    this.setState({ oldValue, newValue, row: updatedRow, column, data })
  }

  onDropdownChange = (evt, obj, oldValue, row, column, rowIndex) => {
    const updatedRow = { ...row, [column.dataField]: obj.value };
    const data = this.state.data.map((item, index) =>
      index === rowIndex ? updatedRow : item
    );
    this.setState({
      oldValue: oldValue,
      row: updatedRow,
      column: column,
      newValue: obj.value,
      data: data,
    })
    this.props.cellEvent(oldValue, obj.value, updatedRow, column)
  }

  render() {
    const { keyField, columns, showHeading, loader, fetchMore, translations, className, style, customLoadingMessage } = this.props
    const { defaultStyle, data, oldValue } = this.state;
    const { onTextAreaFocus, onTextareaBlur, onTextareaChange, onDropdownChange } = this;
    return (
      <div className={`easy-table ${className}`} style={style ? style : defaultStyle}>
        {
          showHeading &&
          <div className='easy-table-heading'>
          </div>
        }

        <InfiniteScroll
          dataLength={data.length}
          next={fetchMore}
          hasMore={true}
          loader={loader}
          height={1}
        >
          <div className='easy-table-body'>
            {
              this.renderColumns(this.props)
            }
            {
              data && data.length > 0 &&
              data.map((row, rowIndex)=>{
                return (
                  <div key={row[keyField]} className={`easy-table-data-row`}>
                    {
                      columns.map((column, colIndex)=>{
                        if(column.hidden) return

                        const parentRef = `${rowIndex}${colIndex}`
                        const dataProps = {
                          key: `data-${column.dataField}`,
                          className: 'easy-table-data-cell',
                          ref: parentRef
                        }
                        if(column.headerStyle){
                          dataProps.style = column.headerStyle
                        }
                        if(column.cellStyle){
                          dataProps.style = column.cellStyle
                        }
                        if(column.classes){
                          const dataClass = this.isFunction(column.classes) ? column.classes(row[column.dataField], row, rowIndex, colIndex) : column.classes
                          dataProps.className = `${dataProps.className} ${dataClass}`
                        }

                        const editable = this.isFunction(column.editable) ? column.editable(row[column.dataField], row, rowIndex, colIndex, column) : column.editable
                        if(editable){
                          dataProps.className = `${dataProps.className} editable`
                        }

                        const isTextarea = column.editor ? column.editor.type === 'textarea' : false;
                        if(isTextarea && editable) {
                          dataProps.className = `${dataProps.className} textarea`
                        }

                        const isDropdown = column.editor ? column.editor.type === 'dropdown' : false;
                        if(isDropdown && editable) {
                          dataProps.className = `${dataProps.className} dropdown`
                        }

                        const isModal = column.editor ? column.editor.type === 'modal' : false
                        if(isModal && editable){
                          dataProps.onClick = (evt) => column.events.onClick(evt, row[column.dataField], row, column, rowIndex, colIndex)
                          dataProps.className = `${dataProps.className} clickable`
                        }

                        const isCustom = column.editor ? column.editor.type === 'custom' : false;
                        if(isCustom && editable) {
                          dataProps.className = `${dataProps.className} custom`
                        }
  
                        if(column.events){
                          if(column.events.onContextMenu){
                            dataProps.onContextMenu = (evt) => column.events.onContextMenu(evt, row[column.dataField], rowIndex)
                            dataProps.onMouseDown = (evt) => column.events.onMouseDown(evt)
                          }
                        }

                        const placeholder = column.editor && column.editor.placeholder ? column.editor.placeholder : "";
                        return (
                          <Fragment key={`col-${column.dataField}`}>
                            {
                              isDropdown &&
                              <DropdownRenderer
                                {...{
                                  ...dataProps,
                                  row,
                                  rowIndex,
                                  column,
                                  oldValue,
                                  onDropdownChange,
                                  editable
                                }}
                              />
                            }
                            {
                              isTextarea &&
                              <TextRenderer
                                {...{
                                  ...dataProps,
                                  placeholder,
                                  row,
                                  rowIndex,
                                  column,
                                  oldValue,
                                  onTextareaChange,
                                  onTextareaBlur,
                                  onTextAreaFocus,
                                  editable
                                }}
                              />
                            }
                            {
                              (isModal || isCustom) &&
                              <div {...dataProps}>
                                {
                                  column.formatter
                                  ?
                                  <div style={column.cellInnerDivStyle}>{column.formatter(row[column.dataField], row, rowIndex, column)}</div>
                                  :
                                  <div style={column.cellInnerDivStyle}>{row[column.dataField]}</div>
                                }
                              </div>
                            }
                            {
                              (!isTextarea && !isDropdown && !isModal && !isCustom) &&
                              <div {...dataProps}>
                                {
                                  column.formatter
                                  ?
                                  <div style={column.cellInnerDivStyle}>{column.formatter(row[column.dataField], row, rowIndex, column)}</div>
                                  :
                                  <div style={column.cellInnerDivStyle}>{row[column.dataField]}</div>
                                }
                              </div>
                            }
                          </Fragment>
                        )
                      })
                    }
                  </div>
                )
              })
            }
            {
              (!data || data.length === 0) &&
              <div className='easy-table-no-data'>{translations && translations.no_data}</div>
            }
          </div>
          {
            customLoadingMessage
            ?
            customLoadingMessage
            :
            <div id="loading-message">
              <i className="loading circle notched icon plan-loading"></i>
              <span>{translations && translations.fetching_activities}</span>
            </div>
          }
        </InfiniteScroll>
      </div>
    )
  }
}



export default EasyTable;



// WEBPACK FOOTER //
// ./src/components/easy_table.js